import { FC, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useGQLQuery } from 'hooks';
import {
  CommunityLocationsSettingsPageQuery,
  Community,
  CommunityLocationsSettingsPageQueryVariables,
  PositionDetails,
  OrderStatusEnum,
  PositionTypeEnum,
} from 'types/graphql';
import { GQLAPIContextType } from 'types/common.types';
import { QUERY_communityLocationsSettingsPage } from 'queries';
import { createGQLDataContext } from 'utils/createDataContext';

export const PositionsAllocationSettingsPageDataContext =
  createGQLDataContext<CommunityLocationsSettingsPageQuery>();

export const PositionsAllocationSettingsPageDataProvider: FC = ({ children }) => {
  const { pathname } = useLocation();
  const facility_id = Number(pathname.split('/')[3]);
  const { data, isLoading, fetch, ...query } = useGQLQuery<
    Community,
    CommunityLocationsSettingsPageQuery,
    CommunityLocationsSettingsPageQueryVariables
  >({
    query: QUERY_communityLocationsSettingsPage,
    key: 'COMMUNITY_LOCATIONS_LOCATION_SETTINGS_PAGE',
    variables: {
      locationId: facility_id,
      type: PositionTypeEnum.primary,
      teamMemberFilter: {
        byPrimaryCommunity: [facility_id],
      },
      orderFilter: {
        forOrders: {
          withStatus: [OrderStatusEnum.pending],
          withCommunity: [facility_id],
        },
      },
    },
  });

  const groupedPositions =
    data &&
    data.fetchCommunity.locationPositions.reduce(
      (
        acc: {
          [key: string]: PositionDetails[];
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        position: any,
      ) => {
        if (position?.department?.name) {
          const departmentName = position.department.name;
          if (!acc[departmentName]) {
            acc[departmentName] = [];
          }
          acc[departmentName].push(position);
        }
        return acc;
      },
      {},
    );

  useEffect(() => {
    fetch();
  }, [fetch]);

  const contextValue = useMemo(
    () => ({ ...query, data, groupedPositions, isLoading, fetch }),
    [data, groupedPositions, isLoading, query, fetch],
  );

  return (
    <PositionsAllocationSettingsPageDataContext.Provider value={contextValue}>
      {children}
    </PositionsAllocationSettingsPageDataContext.Provider>
  );
};

export const usePositionsAllocationSettingsPageDataContext =
  (): GQLAPIContextType<CommunityLocationsSettingsPageQuery> =>
    useContext(PositionsAllocationSettingsPageDataContext);
